/**
 * Politique de mot de passe — miroir de `app/domain/password_policy.py`.
 *
 * Les deux vivent dans des dépôts distincts, donc aucune source commune n'est
 * possible : toute évolution de la règle doit toucher les deux, sinon le
 * formulaire annonce une contrainte que l'API n'applique pas — ou l'inverse, et
 * l'utilisateur se prend un 422 qu'il ne comprend pas.
 *
 * Ce module existe pour qu'il n'y ait qu'UN endroit à changer côté back-office :
 * la règle était jusqu'ici réécrite (mal) dans chaque page, quand elle y était.
 *
 * Classes UNICODE et non ASCII. Le backend s'appuie sur `str.isupper()` et
 * `str.isalnum()` de Python, qui connaissent tout l'Unicode : avec
 * `[^A-Za-z0-9]`, « Aaaaaaaaé » passait côté front et se faisait refuser côté
 * serveur, « é » y étant alphanumérique donc pas un caractère spécial.
 */
export const PASSWORD_MIN_LENGTH = 8;

// Construites à l'exécution plutôt qu'en littéral : la cible TypeScript du
// projet est ES5, qui interdit le drapeau `u` dans un littéral d'expression
// régulière. Le NAVIGATEUR le gère depuis ES2015 — c'est une contrainte du
// compilateur, pas de la plateforme.
const MAJUSCULE = new RegExp('\\p{Lu}', 'u');
const SPECIAL = new RegExp('[^\\p{L}\\p{N}]', 'u');

/** Formulation destinée à l'utilisateur — identique à `DESCRIPTION` du backend. */
export const PASSWORD_DESCRIPTION =
  '8 caractères minimum, dont au moins une majuscule et un caractère spécial.';

/**
 * Renvoie le message d'erreur, ou `null` si le mot de passe convient.
 *
 * Un message par règle plutôt qu'un refus global : « il manque une majuscule »
 * se corrige, « mot de passe invalide » se retente au hasard.
 */
export function passwordError(value: string): string | null {
  if (value.length < PASSWORD_MIN_LENGTH) {
    return `Le mot de passe doit contenir au moins ${PASSWORD_MIN_LENGTH} caractères.`;
  }
  if (!MAJUSCULE.test(value)) {
    return 'Le mot de passe doit contenir au moins une majuscule.';
  }
  // « Spécial » = ni lettre ni chiffre, comme le `isalnum()` du backend.
  if (!SPECIAL.test(value)) {
    return 'Le mot de passe doit contenir au moins un caractère spécial.';
  }
  return null;
}
